import React, { useState, useEffect } from "react";
import { Route, Routes } from "react-router-dom";
// 💡 Add useMediaQuery and Box from MUI
import { useMediaQuery, Tooltip, Box } from "@mui/material";

import Logout from "../pages/Logout.js";
import { useTheme } from "../context/ThemeContext.js";
import Home from "../pages/Home.js";
import ProductionBatch from "../pages/ProductionBatch.js";
import Suppliers from "../pages/Suppliers.js";
import Products from "../pages/Products.js";
import Sidebar from "../components/Sidebar.js";
import Navbar from "../components/Navbar.js";
import Materials from "../pages/Materials.js";
import Orders from "../pages/Orders.js";
import Earnings from "../pages/Earnings.js";
import Categories from "../pages/Categories.js";
import Expenses from "../pages/Expenses.js";
import Customers from "../pages/Customers.js";
import ShippingCompany from "../pages/shippingCompany.js";
import CustomizeTheme from "../pages/CustomizeTheme.js";
import EditProfile from "../pages/EditProfile.js";
import RewardSetting from "../pages/RewardSetting.js";
import FAQs from "../pages/FAQs.js";
import CreateFAQs from "../pages/CreateFAQs.js";
import Career from "../pages/Careers.js";
import Pricing from "../pages/Pricing.js";
import Pages from "../pages/Pages.js";
import EditPage from "../pages/EditPage.js";
import CustomerSupport from "../pages/CustomerSupport.js";
import ContactUsResponses from "../pages/ContactUsResponses.js";
import Complaint from "../pages/Complaint.js";
import subscriptionStatus from "../components/SubscriptionStatus.js";
import UsersPage from "../pages/NewUser/UsersPage.js";
import AddNewUser from "../pages/NewUser/AddNewUser.js";
import EditUserPage from "../pages/NewUser/EditUserPage.js";
import ChangePassword from "../pages/ChangePassword.js";
import ComplaintRequest from "../pages/ComplaintRequest.js";
import Advertiser from "../pages/Advertiser.js";
import AdsManager from "../pages/AdsManager.js";
import CreateAd from "../pages/CreateAd.js";
import AdDetails from "../pages/AdDetails.js";
import AdEdit from "../pages/AdEdit.js";
import Trash from "../pages/Trash.js";
import ZoneManager from "../pages/ZoneManager.js";
import CreateZone from "../pages/CreateZone.js";
import EditZone from "../pages/EditZone.js";
import AdvertiserDashboard from "../pages/AdvertiserDashboard.jsx";
import Marketing from "../pages/Marketing.js";
import Companies from "../pages/Companies.js";
import ModuleBuilder from "../pages/ModuleBuilder.js";
import ModuleCreate from "../pages/ModuleCreate.js";
import AuditLogs from "../pages/AuditLogs.js";
import ModuleEdit from "../pages/ModuleEdit.js";
import ModuleFields from "../pages/ModuleFields.js";
import ModulePermission from "../pages/ModulePermission.js";
import BackupManager from "../pages/BackupManager.js";
import { ImSpades } from "react-icons/im";
import DynamicModulePage from "../pages/DynamicModulePage.js";
import WebhookManager from "../pages/WebhookManager.js";
import ShopSetting from "../pages/ShopSetting.js";
import MasterDashboard from "../pages/MasterDashboard.js";
import PricingPage from "../pages/Site/PricingPage.js";
import PaymentSuccess from "../pages/PaymentSuccess";
import OfflineMessage from "../components/OfflineMessage.js";
import FinancialReports from "../pages/FinancialReports.js";
import SubscriptionAdmin from "../components/SubscriptionAdmin.js";
import {
  storeDataOffline,
  getOfflineData,
  clearOfflineData,
} from "../services/indexedDBHelper.js";

const Dashboard = () => {
  const { theme } = useTheme();

  // 💡 NEW STATE: State for the mobile drawer visibility
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // 💡 NEW HANDLER: Function to open the drawer
  const openDrawer = () => setIsDrawerOpen(true);

  // 💡 Use MUI's hook to detect mobile state (recommended for MUI)
  const isMobileView = useMediaQuery("(max-width:768px)");

  // --- Existing States ---
  // Updated state based on MUI hook
  const [isMobile, setIsMobile] = useState(isMobileView);
  const [showAd, setShowAd] = useState(false);
  const [moduleSwitchCount, setModuleSwitchCount] = useState(0);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [data, setData] = useState([]); // Dynamic data (e.g., for products, orders, customers)
  const [module, setModule] = useState(""); // Handle window resize for mobile responsiveness
  const isSubscribed = subscriptionStatus();

  // --- Updated useEffect for Mobile Responsiveness ---
  useEffect(() => {
    // Keep the internal state synced with the MUI media query hook
    setIsMobile(isMobileView);
  }, [isMobileView]);

  // Handle the online/offline status of the user (Existing logic)
  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOffline(!navigator.onLine);
      if (navigator.onLine && isSubscribed !== "free") {
        syncOfflineData();
      }
    };

    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOnlineStatus);

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOnlineStatus);
    };
  }, [isSubscribed]);

  // Sync offline data with server when the user is back online (Existing logic)
  const syncOfflineData = async () => {
    console.log("isSubscribed", isSubscribed);
    if (isSubscribed === "free") {
      return;
    }

    const data = await getOfflineData();
    if (data.length > 0) {
      const groupedData = {
        add: [],
        update: [],
        delete: [],
      };

      data.forEach((item) => {
        groupedData[item.action].push(item);
      });

      const module = data[0].module;
      const addUrl = `/api/${module}`;
      const updateUrl = `/api/${module}`;
      const deleteUrl = `/api/${module}`;

      try {
        if (groupedData.add.length > 0) {
          await fetch(addUrl, {
            method: "POST",
            body: JSON.stringify(groupedData.add),
            headers: { "Content-Type": "application/json" },
          });
        }

        if (groupedData.update.length > 0) {
          await fetch(updateUrl, {
            method: "PUT",
            body: JSON.stringify(groupedData.update),
            headers: { "Content-Type": "application/json" },
          });
        }

        if (groupedData.delete.length > 0) {
          await fetch(deleteUrl, {
            method: "DELETE",
            body: JSON.stringify(groupedData.delete),
            headers: { "Content-Type": "application/json" },
          });
        }

        console.log("Offline data synced successfully");
        await clearOfflineData();
      } catch (error) {
        console.error("Error syncing offline data:", error);
      }
    }
  };

  // Store data dynamically in IndexedDB (Existing logic)
  const storeOfflineData = async (newData, moduleName, action) => {
    if (isSubscribed === "free") {
      return;
    }

    const dataWithModule = newData.map((item) => ({
      ...item,
      module: moduleName,
      action,
    }));

    await storeDataOffline(dataWithModule);
    console.log(`${action} data saved locally`);
  };

  // The commented out renderOfflineMessage is omitted as requested in the prompt:
  // const renderOfflineMessage = () => {

  //   console.log("renderOfflineMessage", isSubscribed, isOffline);

  //   if (isSubscribed === "free" && isOffline) {

  //     return (

  //       <Tooltip

  //         title={isSubscribed === "free" ? "Upgrade to Premium to Access" : ""}

  //         arrow

  //       >

  //         <div

  //           style={{

  //             position: "fixed", // Use fixed positioning to cover the entire viewport

  //             top: 0,

  //             left: "298px",

  //             right: 0,

  //             bottom: 0,

  //             background: isSubscribed !== "free" ? theme.button : "#d3d3d3",

  //             display: "flex",

  //             alignItems: "center",

  //             justifyContent: "center",

  //             zIndex: 1000, // Ensure it's on top of other elements

  //             pointerEvents: "all", // Make the overlay interactive (for overlay itself)

  //             cursor: isSubscribed === "free" ? "not-allowed" : "pointer", // Make it unclickable for free users

  //           }}

  //         >

  //           <div

  //             style={{

  //               background: "white",

  //               padding: "20px 30px",

  //               borderRadius: "10px",

  //               display: "flex",

  //               alignItems: "center",

  //               justifyContent: "center",

  //               pointerEvents: "none", // Make the content inside the overlay non-interactive

  //             }}

  //           >

  //             <span

  //               style={{

  //                 fontSize: "16px",

  //                 fontWeight: "500",

  //                 color: "#333",

  //                 marginRight: "8px", // Space between the text and icon

  //               }}

  //             >

  //               Upgrade to Premium to access offline features or go online.

  //             </span>

  //             {isSubscribed === "free" && (

  //               <ImSpades

  //                 style={{

  //                   color: "gold",

  //                   fontSize: "20px", // Adjust the size of the icon

  //                   marginLeft: "10px", // Space to the right of the text

  //                 }}

  //               />

  //             )}

  //           </div>

  //         </div>

  //       </Tooltip>

  //     );

  //   }

  //   return null;

  // };

  return (
    <Box
      className="flex min-h-screen w-full"
      sx={{
        background: "#F4F6F9",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* 💡 PASS OPEN HANDLER TO NAVBAR */}
      <Navbar openDrawer={openDrawer} />

      <Box
        className="flex w-full"
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        {/* 💡 PASS DRAWER STATE TO SIDEBAR */}
        <Sidebar
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
        />

        <Box
          component="main"
          className="flex-grow"
          sx={{
            // 💡 ADJUST MARGIN: Use 280px margin for desktop sidebar (assuming it's fixed at 280px)
            // Original code used 248px, updated to 280px for standard expanded width, but keeping 248px to match original logic if it's the correct width
            marginLeft: isMobile ? "0px" : "248px",
            paddingTop: "90px", // Offset fixed Navbar (80px + 10px spacing)
            width: "100%",
            height: "calc(100vh - 90px)",
            overflowY: "auto",
            pointerEvents: !isSubscribed && isOffline ? "none" : "auto",
            transition: "margin-left 0.3s ease",
          }}
        >
          {showAd && <VideoAd onClose={() => setShowAd(false)} />}
          {isOffline && isSubscribed === "free" && (
            <div>You are offline. Subscribe to use offline features.</div>
          )}

          {isSubscribed !== "free" && isOffline && (
            <div
              style={{
                backgroundColor: "#f8d7da",
                color: "#721c24",
                padding: "10px",
                border: "1px solid #f5c6cb",
                borderRadius: "5px",
                fontSize: "16px",
                fontWeight: "bold",
                textAlign: "center",
                marginBottom: "10px",
              }}
            >
              You are offline. Data will be synced once you're online.
            </div>
          )}

          <OfflineMessage
            isSubscribed={isSubscribed}
            isOffline={isOffline}
            theme={theme}
          />

          <Routes>
            <Route path="/Home" element={<Home />} />
            <Route path="/ProductionBatch" element={<ProductionBatch />} />
            <Route path="/Suppliers" element={<Suppliers />} />
            <Route path="/Materials" element={<Materials />} />
            <Route path="/Products" element={<Products />} />
            <Route path="/Orders" element={<Orders />} />
            <Route path="/Earnings" element={<Earnings />} />
            <Route path="/Categories" element={<Categories />} />
            <Route path="/Expenses" element={<Expenses />} />
            <Route path="/Customers" element={<Customers />} />
            <Route path="/ShippingCompany" element={<ShippingCompany />} />
            <Route path="/Logout" element={<Logout />} />
            <Route path="CustomizeTheme" element={<CustomizeTheme />} />
            <Route path="EditProfile" element={<EditProfile />} />
            <Route path="/RewardSetting" element={<RewardSetting />} />
            <Route path="/Complaint" element={<Complaint />} />
            <Route
              path="/ContactUsResponses"
              element={<ContactUsResponses />}
            />
            <Route path="/UsersPage" element={<UsersPage />} />
            <Route path="/addNewUser" element={<AddNewUser />} />
            <Route path="/EditUserPage/:id" element={<EditUserPage />} />
            <Route path="/Career" element={<Career />} />
            <Route path="/Pricing" element={<Pricing />} />
            <Route path="/Pages" exact element={<Pages />} />
            <Route path="/edit-page/:id?" element={<EditPage />} />
            <Route path="/CustomerSupport" element={<CustomerSupport />} />
            <Route path="/FAQs" element={<FAQs />} />
            <Route path="/CreateFAQs" element={<CreateFAQs />} />
            <Route path="/ComplaintRequest" element={<ComplaintRequest />} />
            <Route path="/Advertiser" element={<Advertiser />} />
            <Route path="/Advertiser/ads" element={<AdsManager />} />
            <Route path="/Advertiser/create-ad/" element={<CreateAd />} />
            <Route path="/Advertiser/AdDetails/:id" element={<AdDetails />} />
            <Route path="/Advertiser/AdEdit/:id" element={<AdEdit />} />
            <Route path="/Zones" element={<ZoneManager />} />
            <Route path="/Zones/create" element={<CreateZone />} />
            <Route path="/Zones/edit/:id" element={<EditZone />} />
            <Route path="/Marketing" element={<Marketing />} />
            <Route path="/Companies" element={<Companies />} />
            <Route path="/ModuleBuilder" element={<ModuleBuilder />} />
            <Route path="/ModuleCreate" element={<ModuleCreate />} />

            <Route path="/ModuleEdit/:id?" element={<ModuleEdit />} />
            <Route path="/Financial" element={<FinancialReports />} />
            <Route path="/modules/fields/:id?" element={<ModuleFields />} />
            <Route path="/AuditLogs" element={<AuditLogs />} />
            <Route path="/MasterDashboard" element={<MasterDashboard />} />
            <Route path="/WebhookManager" element={<WebhookManager />} />
            <Route path="/BackupManager" element={<BackupManager />} />
            <Route path="/ShopSetting" element={<ShopSetting />} />
            <Route path="/success" element={<PaymentSuccess />} />
            <Route
              path="/PricingView"
              element={<PricingPage hideSiteNavbar={true} />}
            />
            <Route
              path="/modules/permission/:id?"
              element={<ModulePermission />}
            />
            <Route path="/:moduleSlug" element={<DynamicModulePage />} />
            <Route
              path="/AdvertiserDashboard"
              element={<AdvertiserDashboard />}
            />
            <Route
              path="/admin/subscriptions"
              element={<SubscriptionAdmin />}
            />
            <Route path="/Trash" element={<Trash />} />
            <Route path="/ChangePassword" element={<ChangePassword />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
